import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useRegister } from '@/hooks/useAuth';
import { usePlatformInfo } from '@/hooks/usePlatformInfo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api-client';
import { OAuthButtons } from '@/components/auth/OAuthButtons';
import { formatUsd } from '@/lib/utils';
import { PublicShell } from '@/components/layout/PublicShell';

export function RegisterPage() {
  const navigate = useNavigate();
  const register = useRegister();
  const info = usePlatformInfo();
  const [form, setForm] = useState({ email: '', password: '', full_name: '', organization_name: '' });
  const [error, setError] = useState<string | null>(null);

  const trialAmount = info.data?.trial_micro_usd;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await register.mutateAsync({
        email: form.email,
        password: form.password,
        full_name: form.full_name || undefined,
        organization_name: form.organization_name || undefined,
      });
      navigate({ to: '/dashboard' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Bir hata oluştu');
    }
  };

  return (
    <PublicShell>
      <div className="container flex items-center justify-center py-12">
        <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Hesap Oluştur</CardTitle>
          <CardDescription>
            {trialAmount != null ? (
              <>
                Kayıt olunca{' '}
                <strong className="font-mono text-foreground">{formatUsd(trialAmount, 2)}</strong>{' '}
                hediye bakiye — kart istenmez, süre kısıtı yok.
              </>
            ) : (
              'Hemen hesap oluştur — hediye bakiyen hazır.'
            )}
          </CardDescription>
        </CardHeader>
        <form onSubmit={onSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">Ad Soyad</Label>
              <Input
                id="full_name"
                placeholder="İsim Soyisim"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">E-posta</Label>
              <Input
                id="email"
                type="email"
                required
                placeholder="ornek@mail.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Şifre</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={8}
                placeholder="en az 8 karakter"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="organization_name">Organizasyon Adı (opsiyonel)</Label>
              <Input
                id="organization_name"
                placeholder="boş bırakırsanız isminizden üretilir"
                value={form.organization_name}
                onChange={(e) => setForm({ ...form, organization_name: e.target.value })}
              />
            </div>
            {error && <div className="text-sm text-destructive">{error}</div>}
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button type="submit" className="w-full" disabled={register.isPending}>
              {register.isPending ? 'Kayıt yapılıyor…' : 'Hesap Oluştur'}
            </Button>
            <OAuthButtons mode="register" />
            <div className="text-sm text-muted-foreground">
              Zaten hesabın var mı?{' '}
              <Link to="/login" className="text-primary underline-offset-4 hover:underline">
                Giriş yap
              </Link>
            </div>
          </CardFooter>
        </form>
        </Card>
      </div>
    </PublicShell>
  );
}
